/**
 * 1.6 – AI Hỗ trợ Soạn thảo Văn bản Tố tụng
 * Layout 3 cột: Sidebar (240px) | Editor kiểu Word | AI Panel (320px).
 * Topbar: biểu mẫu, AI Soạn thảo, Xem trước, Lưu & In. Biểu mẫu nhanh (sidebar) → đổi template + nội dung.
 */
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Link,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  IconButton,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  AutoAwesome,
  Visibility,
  Save,
  ChevronRight,
  Refresh,
  Assignment,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Print,
} from '@mui/icons-material'

const CASE_INFO = {
  soHoSo: '2023/TLST-HNGD',
  thamPhan: 'Thẩm phán Trần Văn Bình',
  loai: 'Hôn nhân',
  nguyenDon: 'Lê Hoàng Nam',
  biDon: 'Nguyễn Thị Mai Linh',
  tranhChap: 'Ly hôn',
}

const QUICK_TEMPLATES = [
  'Thông báo thụ lý',
  'Giấy triệu tập',
  'Quyết định đưa vụ án ra xét xử',
  'Biên bản hòa giải',
  'Bản án sơ thẩm',
]

/** Nội dung mẫu điền sẵn theo từng biểu mẫu. */
const TEMPLATE_CONTENT: Record<string, string> = {
  'Thông báo thụ lý': `CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
Độc lập – Tự do – Hạnh phúc

———————————

Số: 01/2026/TB-TLST
Ngày: 01/02/2026

THÔNG BÁO THỤ LÝ VỤ ÁN

Kính gửi: Viện kiểm sát nhân dân quận 1; Nguyên đơn Lê Hoàng Nam; Bị đơn Nguyễn Thị Mai Linh.

Về việc: Vụ án HN-GD-2026-001 – Tranh chấp hôn nhân và gia đình (yêu cầu giải quyết ly hôn).

Tòa án nhân dân quận 1 thông báo đã thụ lý vụ án theo đơn khởi kiện của nguyên đơn. Các bên vui lòng nộp tạm ứng án phí trong thời hạn 15 ngày làm việc kể từ ngày nhận thông báo này theo quy định tại Điều 147 Bộ luật Tố tụng dân sự.

Nơi nhận:
- Viện kiểm sát (để thực hành quyền công tố);
- Nguyên đơn, Bị đơn;
- Lưu hồ sơ vụ án.`,
  'Giấy triệu tập': `CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
Độc lập – Tự do – Hạnh phúc

———————————

Số: 01/2026/GT
Ngày: 15/03/2026

GIẤY TRIỆU TẬP
(Tham gia phiên họp kiểm tra chứng cứ)

Kính gửi:
- Nguyên đơn: Lê Hoàng Nam
- Bị đơn: Nguyễn Thị Mai Linh

Vụ án: HN-GD-2026-001 – Tranh chấp hôn nhân và gia đình (ly hôn).

Tòa án nhân dân quận 1 triệu tập các bên tham gia phiên họp kiểm tra chứng cứ vào:

  Thời gian: 08 giờ 00, ngày 25 tháng 03 năm 2026
  Địa điểm: Phòng xử án số 3 – Tòa án nhân dân quận 1 (hoặc tham gia trực tuyến theo link được gửi qua hệ thống).

Các bên mang theo: CMND/CCCD (bản chính), Giấy chứng nhận kết hôn, các tài liệu chứng cứ đã nộp (bản gốc nếu cần đối chiếu).

Nếu vắng mặt không có lý do chính đáng, Tòa án có thể tiến hành xét xử vắng mặt theo quy định pháp luật.

Nơi nhận:
- Nguyên đơn, Bị đơn (đã nêu);
- Lưu hồ sơ vụ án.`,
  'Quyết định đưa vụ án ra xét xử': `CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
Độc lập – Tự do – Hạnh phúc

———————————

Số: 01/2026/QĐ-ĐVARTX
Ngày: 20/02/2026

QUYẾT ĐỊNH
Đưa vụ án dân sự ra xét xử

Thẩm phán: Thẩm phán Trần Văn Bình – Tòa án nhân dân quận 1.

Căn cứ vào các Điều 203, 204 Bộ luật Tố tụng dân sự;

Xét hồ sơ vụ án HN-GD-2026-001 – Tranh chấp hôn nhân và gia đình (Nguyên đơn: Lê Hoàng Nam; Bị đơn: Nguyễn Thị Mai Linh). Sau khi thụ lý, Tòa án đã tiến hành hòa giải nhưng các bên không thỏa thuận được. Hồ sơ đã đủ điều kiện đưa vụ án ra xét xử.

QUYẾT ĐỊNH:

Đưa vụ án dân sự số HN-GD-2026-001 ra xét xử sơ thẩm tại Tòa án nhân dân quận 1. Thời gian và địa điểm phiên tòa sẽ được thông báo bằng Giấy triệu tập riêng.

Quyết định có hiệu lực kể từ ngày ký.`,
  'Biên bản hòa giải': `CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
Độc lập – Tự do – Hạnh phúc

———————————

BIÊN BẢN HÒA GIẢI
(Vụ án dân sự)

Số: 01/2026/BB-HG
Ngày: 18/02/2026 – Tại Phòng hòa giải, Tòa án nhân dân quận 1.

Thành phần:
- Thẩm phán chủ trì: Thẩm phán Trần Văn Bình
- Thư ký: Thư ký Nguyễn Thị Hương
- Nguyên đơn: Lê Hoàng Nam (có mặt)
- Bị đơn: Nguyễn Thị Mai Linh (có mặt)

Vụ án: HN-GD-2026-001 – Yêu cầu giải quyết ly hôn.

Nội dung hòa giải: Thẩm phán đã giải thích quyền, nghĩa vụ và thủ tục hòa giải. Hai bên trình bày ý kiến. Tòa án đề xuất phương án hòa giải nhưng các bên không đạt được thỏa thuận.

Kết quả: Hòa giải không thành. Tòa án lập biên bản này, các bên ký xác nhận và chuyển hồ sơ sang giai đoạn chuẩn bị xét xử.

Biên bản được lập xong, đọc lại cho các bên cùng nghe, các bên đồng ý và ký tên dưới đây.`,
  'Bản án sơ thẩm': `CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
Độc lập – Tự do – Hạnh phúc

———————————

BẢN ÁN DÂN SỰ SƠ THẨM
(Số: 01/2026/BA-DS-ST)

Vụ án: Tranh chấp ly hôn – HN-GD-2026-001

Nguyên đơn: Lê Hoàng Nam.
Bị đơn: Nguyễn Thị Mai Linh.

Ngày mở phiên: 25/03/2026. Thẩm phán chủ trì: Thẩm phán Trần Văn Bình.

Căn cứ vào biên bản phiên tòa, chứng cứ và lời khai các bên; Căn cứ Điều 51, 56 Luật Hôn nhân và gia đình 2014; Điều 147, 203 Bộ luật Tố tụng dân sự 2015;

XÉT XỬ:

[Phần nhận định của Tòa án – Thẩm phán soạn dựa trên chứng cứ và áp dụng pháp luật. Gợi ý: Tra cứu điều luật, án lệ tại Công cụ AI Research (Pháp luật & Án lệ) để tham chiếu.]

QUYẾT ĐỊNH

1. Về ly hôn: Chấp nhận yêu cầu ly hôn của nguyên đơn. Hôn nhân chấm dứt kể từ ngày bản án có hiệu lực.

2. Về con chung: [Giao con cho…, cấp dưỡng… – điền theo tình tiết vụ án.]

3. Về tài sản chung: [Phân chia theo thỏa thuận hoặc theo quyết định của Tòa – điền theo chứng cứ.]

4. Án phí: [Nguyên đơn/Bị đơn chịu – theo Điều 147 BLTTDS.]

Bản án có thể bị kháng cáo trong thời hạn 15 ngày kể từ ngày tuyên án.`,
}

const AI_SUGGESTIONS = [
  'Viện kiểm sát nhân dân Quận X',
  'Tranh chấp hôn nhân và gia đình',
  'Theo mẫu số 01-DS',
]

/** Lựa chọn Kính gửi cho dropdown AI panel */
const KINH_GUI_OPTIONS = [
  'Viện kiểm sát nhân dân quận 1; Nguyên đơn Lê Hoàng Nam; Bị đơn Nguyễn Thị Mai Linh',
  'Viện kiểm sát nhân dân Quận X; Nguyên đơn; Bị đơn',
  'Viện kiểm sát nhân dân quận 2; Các đương sự theo hồ sơ',
]

const DEFAULT_TEMPLATE = 'Thông báo thụ lý'
const initialContent = TEMPLATE_CONTENT[DEFAULT_TEMPLATE] ?? ''

/** Các cụm từ AI điền (nền vàng) theo từng biểu mẫu – phải là chuỗi con đúng trong nội dung. */
const TEMPLATE_SUGGESTIONS: Record<string, string[]> = {
  'Thông báo thụ lý': ['Viện kiểm sát nhân dân quận 1', 'Điều 147 Bộ luật Tố tụng dân sự'],
  'Giấy triệu tập': ['08 giờ 00, ngày 25 tháng 03 năm 2026', 'Phòng xử án số 3 – Tòa án nhân dân quận 1'],
  'Quyết định đưa vụ án ra xét xử': ['Điều 203, 204 Bộ luật Tố tụng dân sự', 'Thẩm phán Trần Văn Bình – Tòa án nhân dân quận 1'],
  'Biên bản hòa giải': ['Phòng hòa giải, Tòa án nhân dân quận 1', 'Thư ký Nguyễn Thị Hương'],
  'Bản án sơ thẩm': ['Điều 51, 56 Luật Hôn nhân và gia đình 2014', 'Điều 147, 203 Bộ luật Tố tụng dân sự 2015'],
}

/** Khổ trang A4 (mm). */
const A4_WIDTH_MM = 210
const A4_HEIGHT_MM = 297
const MM_TO_PX = 3.78

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

interface Range { start: number; end: number; phrase: string }

function getSuggestionRanges(content: string, phrases: string[]): Range[] {
  const ranges: Range[] = []
  for (const phrase of phrases) {
    let i = 0
    while (i < content.length) {
      const idx = content.indexOf(phrase, i)
      if (idx === -1) break
      ranges.push({ start: idx, end: idx + phrase.length, phrase })
      i = idx + phrase.length
    }
  }
  ranges.sort((a, b) => a.start - b.start)
  const merged: Range[] = []
  for (const r of ranges) {
    if (merged.length && r.start < merged[merged.length - 1].end) continue
    merged.push(r)
  }
  return merged
}

function buildEditorHtml(content: string, ranges: Range[]): string {
  const segments: { text: string; isSuggestion: boolean }[] = []
  let pos = 0
  for (const r of ranges) {
    if (r.start > pos) segments.push({ text: content.slice(pos, r.start), isSuggestion: false })
    segments.push({ text: content.slice(r.start, r.end), isSuggestion: true })
    pos = r.end
  }
  if (pos < content.length) segments.push({ text: content.slice(pos), isSuggestion: false })
  const toHtml = (s: string) => escapeHtml(s).replace(/\n/g, '<br>')
  return segments
    .map((s) => (s.isSuggestion ? `<span class="ai-suggestion">${toHtml(s.text)}</span>` : toHtml(s.text)))
    .join('')
}

function getCaretOffset(container: Node): number {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return 0
  const range = sel.getRangeAt(0)
  const pre = document.createRange()
  pre.selectNodeContents(container)
  pre.setEnd(range.startContainer, range.startOffset)
  return pre.toString().length
}

function setCaretToOffset(container: HTMLElement, offset: number): void {
  const sel = window.getSelection()
  if (!sel) return
  let current = 0
  const walk = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = (node.textContent || '').length
      if (current + len >= offset) {
        const range = document.createRange()
        range.setStart(node, Math.min(offset - current, len))
        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)
        return true
      }
      current += len
      return false
    }
    for (let i = 0; i < node.childNodes.length; i++) {
      if (walk(node.childNodes[i])) return true
    }
    return false
  }
  walk(container)
}

function AIDraftPage() {
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [aiPanelOpen, setAiPanelOpen] = useState(true)
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE)
  const [docContent, setDocContent] = useState(initialContent)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity?: 'success' | 'info' | 'warning' }>({ open: false, message: '' })
  const [bold, setBold] = useState(false)
  const [italic, setItalic] = useState(false)
  const [underline, setUnderline] = useState(false)
  const [align, setAlign] = useState<'left' | 'center' | 'right' | 'justify'>('left')
  const [fontSize, setFontSize] = useState(14)
  const [fontFamily, setFontFamily] = useState('Times New Roman')
  const [suggestionPhrases, setSuggestionPhrases] = useState<string[]>(() => TEMPLATE_SUGGESTIONS[DEFAULT_TEMPLATE] ?? [])
  const [aiKinhGui, setAiKinhGui] = useState(KINH_GUI_OPTIONS[0])
  const [aiVeViec, setAiVeViec] = useState('Vụ án HN-GD-2026-001 – Tranh chấp hôn nhân và gia đình (yêu cầu giải quyết ly hôn).')
  const editorRef = useRef<HTMLDivElement>(null)
  const caretRef = useRef<number | null>(null)

  useEffect(() => {
    const content = TEMPLATE_CONTENT[template]
    if (content) {
      setDocContent(content)
      setSuggestionPhrases(TEMPLATE_SUGGESTIONS[template] ?? [])
      const kinhGuiMatch = content.match(/Kính gửi:\s*([^\n]+)/)
      const veViecMatch = content.match(/Về việc:\s*([^\n]+)/)
      if (kinhGuiMatch) setAiKinhGui(kinhGuiMatch[1].trim())
      if (veViecMatch) setAiVeViec(veViecMatch[1].trim())
    }
  }, [template])

  const handleQuickTemplate = (t: string) => {
    setTemplate(t)
  }

  const handleAiDraft = () => {
    setSnack({ open: true, message: 'Đang tạo gợi ý soạn thảo...', severity: 'info' })
    setTimeout(() => setSnack((s) => ({ ...s, message: 'Đã áp dụng gợi ý AI vào văn bản.', severity: 'success' })), 1500)
  }

  const handlePreview = () => setPreviewOpen(true)
  const handleSavePrint = () => {
    setSnack({ open: true, message: 'Đã lưu bản nháp. Chuyển sang chế độ in.', severity: 'success' })
  }

  const handleFillFromCase = () => {
    setDocContent((prev) => {
      let next = prev
      next = next.replace(/\{\{soHoSo\}\}/g, CASE_INFO.soHoSo)
      next = next.replace(/\{\{thamPhan\}\}/g, CASE_INFO.thamPhan)
      next = next.replace(/\{\{nguyenDon\}\}/g, CASE_INFO.nguyenDon)
      next = next.replace(/\{\{biDon\}\}/g, CASE_INFO.biDon)
      next = next.replace(/\{\{loai\}\}/g, CASE_INFO.loai)
      next = next.replace(/\{\{tranhChap\}\}/g, CASE_INFO.tranhChap)
      if (next === prev) {
        setSnack({ open: true, message: 'Đã điền thông tin vụ án vào các vị trí phù hợp trong văn bản.', severity: 'success' })
        return prev
      }
      setSnack({ open: true, message: 'Đã điền thông tin vụ án vào văn bản.', severity: 'success' })
      return next
    })
  }

  const handleApplyKinhGui = () => {
    setDocContent((prev) => prev.replace(/Kính gửi:\s*[^\n]+/, 'Kính gửi: ' + aiKinhGui))
    setSnack({ open: true, message: 'Đã điền "Kính gửi" vào văn bản.', severity: 'success' })
  }

  const handleApplyVeViec = () => {
    setDocContent((prev) => prev.replace(/Về việc:\s*[^\n]+/, 'Về việc: ' + aiVeViec))
    setSnack({ open: true, message: 'Đã điền "Về việc" vào văn bản.', severity: 'success' })
  }

  const handleApplyAiPanel = () => {
    setDocContent((prev) => {
      let next = prev.replace(/Kính gửi:\s*[^\n]+/, 'Kính gửi: ' + aiKinhGui)
      next = next.replace(/Về việc:\s*[^\n]+/, 'Về việc: ' + aiVeViec)
      return next
    })
    setSnack({ open: true, message: 'Đã áp dụng Kính gửi & Về việc vào văn bản.', severity: 'success' })
  }

  const handleApplyMauSo = () => {
    setDocContent((prev) => {
      if (prev.includes('theo quy định tại Điều 147')) {
        return prev.replace('theo quy định tại Điều 147 Bộ luật Tố tụng dân sự.', 'theo quy định tại Điều 147 Bộ luật Tố tụng dân sự (theo mẫu số 01-DS).')
      }
      return prev + '\n\nTheo mẫu số 01-DS.'
    })
    setSnack({ open: true, message: 'Đã áp dụng gợi ý mẫu số 01-DS vào văn bản.', severity: 'success' })
  }

  const handleApplySuggestionChip = (text: string) => {
    if (text === 'Viện kiểm sát nhân dân Quận X') {
      setDocContent((prev) => prev.replace(/Viện kiểm sát nhân dân quận 1/g, 'Viện kiểm sát nhân dân Quận X'))
    } else if (text === 'Tranh chấp hôn nhân và gia đình') {
      setDocContent((prev) => prev.replace(/Tranh chấp hôn nhân và gia đình \([^)]+\)/g, 'Tranh chấp hôn nhân và gia đình'))
    } else if (text === 'Theo mẫu số 01-DS') {
      handleApplyMauSo()
      return
    }
    setSnack({ open: true, message: `Đã chèn gợi ý: "${text}"`, severity: 'success' })
  }

  const suggestionRanges = getSuggestionRanges(docContent, suggestionPhrases)

  const handleEditorInput = () => {
    const el = editorRef.current
    if (!el) return
    caretRef.current = getCaretOffset(el)
    setDocContent(el.innerText)
  }

  const handleEditorKeyDown = (e: React.KeyboardEvent) => {
    const el = editorRef.current
    if (!el) return
    const currentText = el.innerText || ''
    const ranges = getSuggestionRanges(currentText, suggestionPhrases)
    if (ranges.length === 0) return
    const caret = getCaretOffset(el)
    const hit = ranges.find((r) => caret >= r.start && caret <= r.end)
    if (!hit) return
    if (e.key === 'Tab') {
      e.preventDefault()
      setDocContent(currentText)
      setSuggestionPhrases((prev) => prev.filter((p) => p !== hit.phrase))
      setSnack({ open: true, message: 'Đã chấp nhận gợi ý.', severity: 'success' })
    } else if (e.key === 'Escape') {
      e.preventDefault()
      const newText = currentText.slice(0, hit.start) + currentText.slice(hit.end)
      caretRef.current = hit.start
      setDocContent(newText)
      setSuggestionPhrases((prev) => prev.filter((p) => p !== hit.phrase))
      setSnack({ open: true, message: 'Đã bỏ qua gợi ý.', severity: 'info' })
    }
  }

  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    const ranges = getSuggestionRanges(docContent, suggestionPhrases)
    const html = buildEditorHtml(docContent, ranges)
    if (el.innerHTML !== html) el.innerHTML = html
    if (caretRef.current !== null) {
      setCaretToOffset(el, caretRef.current)
      caretRef.current = null
    }
  }, [docContent, suggestionPhrases])

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)', overflow: 'hidden' }}>
      {/* Topbar */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={600}>UEL E-Court</Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Chọn biểu mẫu</InputLabel>
          <Select value={template} label="Chọn biểu mẫu" onChange={(e) => setTemplate(e.target.value || DEFAULT_TEMPLATE)}>
            <MenuItem value="">— Chọn —</MenuItem>
            {QUICK_TEMPLATES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" size="small" startIcon={<AutoAwesome />} sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }} onClick={handleAiDraft}>
          AI Soạn thảo ✨
        </Button>
        <Button variant="outlined" size="small" startIcon={<Visibility />} onClick={handlePreview}>Xem trước</Button>
        <Button variant="contained" size="small" startIcon={<Save />} color="primary" onClick={handleSavePrint}>Lưu & In</Button>
      </Paper>

      {template === 'Bản án sơ thẩm' && (
        <Alert severity="info" sx={{ mx: 2, mt: 1 }} icon={<AutoAwesome />}>
          Tra cứu điều luật, án lệ tại{' '}
          <Link component="button" variant="body2" onClick={() => navigate('/workspace/ai-research')} sx={{ fontWeight: 600 }}>
            Công cụ AI Research (Pháp luật & Án lệ)
          </Link>
          {' '}để tham chiếu khi soạn bản án.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Sidebar trái – Thông tin vụ án (240px) */}
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          {!sidebarOpen && (
            <Box sx={{ width: 28, borderRight: 1, borderColor: 'divider', display: 'flex', alignItems: 'flex-start', pt: 2 }}>
              <IconButton size="small" onClick={() => setSidebarOpen(true)} sx={{ transform: 'rotate(180deg)' }}><ChevronRight /></IconButton>
            </Box>
          )}
          <Paper elevation={0} sx={{ width: sidebarOpen ? 240 : 0, borderRight: 1, borderColor: 'divider', overflow: 'hidden', transition: 'width 0.2s' }}>
          <Collapse in={sidebarOpen} orientation="horizontal">
            <Box sx={{ p: 2, width: 240 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>📁 Thông tin vụ án</Typography>
                <IconButton size="small" onClick={() => setSidebarOpen(false)}><ChevronRight /></IconButton>
              </Box>
              <Box sx={{ fontSize: 13, '& > div': { display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.5 }, '& strong': { color: 'text.secondary' } }}>
                <div><strong>Số hồ sơ:</strong> <span>{CASE_INFO.soHoSo}</span></div>
                <div><strong>Thẩm phán:</strong> <span>{CASE_INFO.thamPhan}</span></div>
                <div><strong>Loại:</strong> <span>{CASE_INFO.loai}</span></div>
                <div><strong>Nguyên đơn:</strong> <span>{CASE_INFO.nguyenDon}</span></div>
                <div><strong>Bị đơn:</strong> <span>{CASE_INFO.biDon}</span></div>
                <div><strong>Tranh chấp:</strong> <span>{CASE_INFO.tranhChap}</span></div>
              </Box>
              <Button size="small" variant="outlined" fullWidth sx={{ mt: 1.5 }} onClick={handleFillFromCase}>
                Dùng để điền tự động
              </Button>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Biểu mẫu nhanh</Typography>
              {QUICK_TEMPLATES.map((t) => (
                <Box
                  key={t}
                  onClick={() => handleQuickTemplate(t)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    py: 0.75,
                    px: 0.5,
                    cursor: 'pointer',
                    borderRadius: 1,
                    bgcolor: template === t ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Assignment sx={{ fontSize: 16, color: template === t ? 'primary.main' : 'text.secondary' }} />
                  <Typography variant="body2" fontWeight={template === t ? 600 : 400}>{t}</Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
          </Paper>
        </Box>

        {/* Editor trung tâm – kiểu Word */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', bgcolor: 'grey.100' }}>
          {/* Thanh công cụ định dạng (ribbon) */}
          <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', px: 1.5, py: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: 13, height: 32 }}
                >
                  <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                  <MenuItem value="Arial">Arial</MenuItem>
                  <MenuItem value="Calibri">Calibri</MenuItem>
                </Select>
              </FormControl>
              <Select
                size="small"
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                sx={{ minWidth: 56, height: 32, fontSize: 13 }}
              >
                {[12, 13, 14, 16, 18, 20, 24].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <ToggleButtonGroup size="small" value={[bold && 'bold', italic && 'italic', underline && 'underline'].filter(Boolean)} sx={{ height: 32 }}>
                <ToggleButton value="bold" aria-label="Đậm" onClick={() => setBold(!bold)}>
                  <FormatBold />
                </ToggleButton>
                <ToggleButton value="italic" aria-label="Nghiêng" onClick={() => setItalic(!italic)}>
                  <FormatItalic />
                </ToggleButton>
                <ToggleButton value="underline" aria-label="Gạch chân" onClick={() => setUnderline(!underline)}>
                  <FormatUnderlined />
                </ToggleButton>
              </ToggleButtonGroup>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <ToggleButtonGroup size="small" value={align} exclusive onChange={(_, v) => v && setAlign(v)} sx={{ height: 32 }}>
                <ToggleButton value="left" aria-label="Căn trái"><FormatAlignLeft /></ToggleButton>
                <ToggleButton value="center" aria-label="Giữa"><FormatAlignCenter /></ToggleButton>
                <ToggleButton value="right" aria-label="Căn phải"><FormatAlignRight /></ToggleButton>
                <ToggleButton value="justify" aria-label="Đều"><FormatAlignJustify /></ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Paper>
          {/* Ruler giả lập (vạch cm) */}
          <Box sx={{ px: 2, py: 0.5, bgcolor: 'grey.200', borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'flex-end', gap: 0, minHeight: 24 }}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((i) => (
              <Box key={i} sx={{ width: A4_WIDTH_MM * MM_TO_PX / 20, textAlign: 'center', fontSize: 10, color: 'text.secondary' }}>
                {i % 5 === 0 ? i : ''}
              </Box>
            ))}
          </Box>
          {/* Vùng trang A4 */}
          <Box sx={{ flex: 1, overflow: 'auto', py: 2, px: 2, display: 'flex', justifyContent: 'center' }}>
            <Paper
              elevation={2}
              sx={{
                width: A4_WIDTH_MM * MM_TO_PX,
                minHeight: A4_HEIGHT_MM * MM_TO_PX,
                maxWidth: '100%',
                bgcolor: '#fff',
                p: 3,
                boxSizing: 'border-box',
                '& .ai-suggestion': {
                  backgroundColor: 'rgba(255, 235, 59, 0.35)',
                  padding: '0 2px',
                  borderRadius: 1,
                },
              }}
            >
              <Box
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditorInput}
                onKeyDown={handleEditorKeyDown}
                sx={{
                  fontFamily: fontFamily + ', serif',
                  fontSize: fontSize,
                  lineHeight: 1.8,
                  fontWeight: bold ? 700 : 400,
                  fontStyle: italic ? 'italic' : 'normal',
                  textDecoration: underline ? 'underline' : 'none',
                  textAlign: align,
                  minHeight: 400,
                  outline: 'none',
                  '&:empty::before': { content: '"Nhập nội dung..."', color: 'text.disabled' },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Các trường AI điền có nền vàng nhạt. Nhấn <strong>Tab</strong> để chấp nhận gợi ý, <strong>Esc</strong> để bỏ qua.
              </Typography>
            </Paper>
          </Box>
        </Box>

        {/* AI Panel phải (320px) */}
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          <Paper elevation={0} sx={{ width: aiPanelOpen ? 320 : 0, borderLeft: 1, borderColor: 'divider', overflow: 'hidden', transition: 'width 0.2s' }}>
          <Collapse in={aiPanelOpen} orientation="horizontal">
            <Box sx={{ p: 2, width: 320, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>✨ AI Tự điền & Gợi ý động</Typography>
                <IconButton size="small" onClick={() => setAiPanelOpen(false)}><ChevronRight /></IconButton>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Kính gửi:</Typography>
                <Select
                  size="small"
                  fullWidth
                  value={aiKinhGui}
                  onChange={(e) => setAiKinhGui(e.target.value)}
                  displayEmpty
                  sx={{ mt: 0.5 }}
                >
                  {KINH_GUI_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt} sx={{ whiteSpace: 'normal' }}>{opt}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Về việc:</Typography>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={aiVeViec}
                  onChange={(e) => setAiVeViec(e.target.value)}
                  placeholder="Tranh chấp hôn nhân và gia đình"
                  sx={{ mt: 0.5 }}
                />
                <Button size="small" variant="outlined" fullWidth sx={{ mt: 1 }} onClick={handleApplyAiPanel}>
                  Áp dụng vào văn bản
                </Button>
              </Box>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2">💡 Có thể điền nội dung theo mẫu số 01-DS?</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button size="small" variant="contained" onClick={handleApplyMauSo}>Áp dụng</Button>
                  <Button size="small" onClick={() => setSnack({ open: true, message: 'Đã bỏ qua gợi ý.', severity: 'info' })}>Bỏ qua</Button>
                </Box>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>Gợi ý thông minh</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, overflow: 'auto' }}>
                {AI_SUGGESTIONS.map((s) => (
                  <Chip key={s} label={s} size="small" onClick={() => handleApplySuggestionChip(s)} sx={{ mb: 0.5, cursor: 'pointer' }} />
                ))}
              </Box>
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button size="small" startIcon={<Refresh />} onClick={() => setSnack({ open: true, message: 'Đã làm mới gợi ý.', severity: 'info' })}>Làm mới gợi ý</Button>
                <Button size="small" startIcon={<Assignment />} onClick={() => { setDocContent(TEMPLATE_CONTENT[template] ?? docContent); setSuggestionPhrases(TEMPLATE_SUGGESTIONS[template] ?? []); setSnack({ open: true, message: 'Đã nạp lại nội dung biểu mẫu.', severity: 'success' }); }}>Dùng toàn bộ template</Button>
              </Box>
            </Box>
          </Collapse>
          </Paper>
          {!aiPanelOpen && (
            <Box sx={{ width: 28, borderLeft: 1, borderColor: 'divider', display: 'flex', alignItems: 'flex-start', pt: 2 }}>
              <IconButton size="small" onClick={() => setAiPanelOpen(true)}><ChevronRight /></IconButton>
            </Box>
          )}
        </Box>
      </Box>

      {/* Dialog Xem trước */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '80vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Xem trước – {template}</span>
          <Box>
            <Button size="small" startIcon={<Print />} onClick={() => { setSnack({ open: true, message: 'Đã gửi in.', severity: 'success' }); setPreviewOpen(false); }}>In</Button>
            <Button size="small" onClick={() => setPreviewOpen(false)}>Đóng</Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#fff', maxWidth: A4_WIDTH_MM * MM_TO_PX, mx: 'auto' }}>
            <Typography component="pre" sx={{ fontFamily: 'Times New Roman, serif', fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap', textAlign: 'left' }}>
              {docContent}
            </Typography>
          </Paper>
        </DialogContent>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity || 'info'} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AIDraftPage
